import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { MiscService } from 'app/shared/misc.service';
import { ToastrService } from 'ngx-toastr';
import { of } from 'rxjs';
import { catchError, exhaustMap, map, mergeMap, tap } from 'rxjs/operators';
import { SettingsService } from '../settings.service';
import * as settingsActions from './settings.actions';

@Injectable({
  providedIn: 'root'
})
export class SettingsEffects {
  constructor(
    private actions$: Actions,
    private settingsService: SettingsService,
    private toastr: ToastrService,
    private miscService: MiscService
  ) {}

  editBusinessName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.editBusinessName),
      exhaustMap((action) =>
        this.settingsService.changeBusinessName(action.businessNameDto).pipe(
          exhaustMap((response: any) => [settingsActions.editBusinessNameSuccess()]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(settingsActions.editBusinessNameFailed());
          })
        )
      )
    )
  );

  changePassword$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.changePassword),
      exhaustMap((action) =>
        this.settingsService.changePassword(action.changePasswordDto).pipe(
          exhaustMap((response: any) => [settingsActions.changePasswordSuccess()]),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured');
            return of(settingsActions.changePasswordFailed());
          })
        )
      )
    )
  );

  generateAccountStatement$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.generateAccountStatement),
      exhaustMap((action) =>
        this.settingsService
          .generateAccountStatement(action.accountStatementDto, action.documentType, action.walletNumber)
          .pipe(
            exhaustMap((response: any) => [settingsActions.generateAccountStatementSuccess()]),
            catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occured');
              return of(settingsActions.generateAccountStatementFailed());
            })
          )
      )
    )
  );



  createRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.createRole),
      exhaustMap((action) =>
        this.settingsService.createUserRoles(action.RoleDto).pipe(
          exhaustMap((response: any) => [settingsActions.createRoleSuccess(response), settingsActions.fetchRoles()]),
          tap((res) => {
            this.toastr.success('Congratulations! You have successfully created a new role');
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.message, 'An error occurred');
            return of(settingsActions.createRoleFailed());
          })
        )
      )
    )
  );

  createUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.createUser),
      exhaustMap((action) =>
        this.settingsService.createNewUser(action.userCreateDto).pipe(
          // The API returns HTTP 200 with { success:false } on failure, so a plain 200
          // is not enough — surface the backend message instead of a false success toast.
          tap((response: any) => {
            if (response?.success === false) {
              throw new Error(response?.message || 'Failed to create subuser');
            }
            this.toastr.success('Congratulations! user has been created successful.NB:The invitation sent to user is only valid till 7 days');
          }),
          mergeMap((response: any) => of(
            settingsActions.createUserSuccess(response),
            settingsActions.fetchUsers({ page: 1, limit: 10, filter: '' })
          )),
          catchError((error: any) => {
            this.toastr.error(error?.error?.message || error?.message || 'Failed to create subuser', 'An error occurred');
            return of(settingsActions.createUserFailed());
          })
        )
      )
    )
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.editUser),
      exhaustMap((action) =>
        this.settingsService.updateUser(action.UsersDto).pipe(
          tap((response: any) => {
            if (response?.success === false) {
              throw new Error(response?.message || 'Failed to update user');
            }
            this.toastr.success('Congratulations!, you have successfully updated this user information');
          }),
          mergeMap((response: any) => of(
            settingsActions.editUserSuccess(response),
            settingsActions.fetchUsers({ page: 1, limit: 10, filter: '' })
          )),
          catchError((error: any) => {
            this.toastr.error(error?.error?.message || error?.message || 'Failed to update user', 'An error occurred');
            return of(settingsActions.editUserFailed());
          })
        )
      )
    )
  );

  updateRole$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.editRole),
      exhaustMap((action) =>
        this.settingsService.updateRoleDetails(action.RoleDto).pipe(
          map((response: any) => settingsActions.editRoleSuccess(response), settingsActions.fetchRoles()),
          tap((response: any) => {
            this.toastr.success('Congratulations!, you have successfully updated role');
          }),
          mergeMap(() => of(settingsActions.fetchRoles())),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.message, 'An error occurred');
            return of(settingsActions.editRoleFailed());
          })
        )
      )
    )
  );

  deleteSystemUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.deleteUser),
      exhaustMap((action) =>
        this.settingsService.deleteUser(action.UsersDto).pipe(
          tap((response: any) => {
            if (response?.success === false) {
              throw new Error(response?.message || 'Failed to delete user');
            }
            this.toastr.success('Congratulations!, user successfully deleted');
          }),
          mergeMap((response: any) => of(
            settingsActions.deleteUserSuccess(response),
            settingsActions.fetchUsers({ page: 1, limit: 10, filter: '' })
          )),
          catchError((error: any) => {
            this.toastr.error(error?.error?.message || error?.message || 'Failed to delete user', 'An error occurred');
            return of(settingsActions.deleteUserFailed());
          })
        )
      )
    )
  );

  fetchAllUserRoles$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.fetchRoles),
      exhaustMap((action) =>
        this.settingsService.fetchAsaRoles().pipe(
          map((response: any) => {
            return settingsActions.fetchRolesSuccess({ allRoles: response });
          }),
          catchError((error: HttpErrorResponse) => {
            this.toastr.error(error.error?.errors, 'An error occured, merchant roles could not be fetched');
            return of(settingsActions.fetchRolesFailed());
          })
        )
      )
    )
  );

  fetchAllUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(settingsActions.fetchUsers),
      exhaustMap((action) => {
        return this.settingsService
          .fetchAllUsers(this.miscService.getMerchantDetails().merchant_id, action.page, action.limit, action.filter)
          .pipe(
            map((response: any) => settingsActions.fetchUsersSuccess({ allUsers: response })),
            catchError((error: HttpErrorResponse) => {
              this.toastr.error(error.error?.errors, 'An error occurred, merchant roles could not be fetched');
              return of(settingsActions.fetchUsersFailed());
            })
          );
      })
    )
  );

  validateUserPassword(merchantId: string, email: string) {
    this.settingsService.validatePassword(merchantId, email).subscribe({
      next: (res) => {

      },
      error: (error) => {
      },
      complete: () => {
      }
    });
  }
}
